/**
 * System.Ledger.Definition.types
 *
 * The parametric layer of the reconciliation ledger: a `LedgerDefinition` names roles (claim
 * amount, match group, counterparty, trigger date, claim kind, grain ref, quantity, dimensions) and
 * binds each to form types, ArTable columns, fields and entity references, so no customer id or
 * column ever appears in code. A role reads the line's own cell or reference hop, a same-form table
 * lookup, or the first of several such sources that binds; no tenant's table shape is assumed.
 * Consumers: `validateLedgerDefinition`, `evaluateLedgerAccrualBinding` and `ledgerBindSources` in
 * `ledgerEngine`, the accrual worker (SYD-2707) and `resolveLedgerClaimTerms` (SYD-2709). Calendar
 * structure (zone and fiscal period) is build config, never a definition member, because it
 * decides which period every past transaction belongs to. Moments are ISO-8601 strings parsed at the
 * edge. Definitions live in Firestore at `ledgerDefinitions/{ledgerId}`; clients write them only through
 * the `ledgerSaveDefinition` callable, never directly. A `file` channel holds one `LedgerStatementMapping`
 * per counterparty's statement format, read by `ledgerStatementReceipt` into one receipt per file.
 * `aging` also holds the unapplied-cash threshold `ledgerListExceptions` reads.
 */

import type { UserRightId } from './AccessControl.types';
import type { DateLocale, NumberLocale } from './System.Agent.ExtractionCoercion.types';
import type { ArRowId, ArTableId, TypeId } from './System.Artifacts.types';
import type { FormDeriveRowReferencePropSource, FormDeriveSourceCellSource } from './System.FormDerive.types';
import type { LedgerInstrument } from './System.Ledger.types';
import type { WorkflowRowFilter } from './System.Workflow.types';

export const LEDGER_DEFINITIONS_COLLECTION = 'ledgerDefinitions';

/** An ISO-8601 instant carrying `Z` or an explicit offset. */
export type LedgerMoment = string;

export type LedgerFiscalPeriodUnit = 'week' | 'month' | 'quarter';

/** Tenant build config: the zone periods are computed in, and the period grid's unit and anchor. */
export type LedgerCalendarStructure = {
    timeZone: string;
    fiscalPeriod: { unit: LedgerFiscalPeriodUnit; anchor: LedgerMoment };
};

export type LedgerCalendarSettings = {
    /** Accruals triggered before this moment are never posted. */
    startAt: LedgerMoment;
    close: { cycle: 'none' | 'period'; graceDays: number };
};

export type LedgerDimension = { dimensionId: string; label: string; valueType: 'text' | 'number' };

export type LedgerClaimKind = { claimKindId: string; label: string };

/** One key pair of a table lookup: the lookup row's column equals the source line's column, compared by `canonicalizeFormDeriveValue`. */
export type LedgerTableLookupKey = { lookupTypeId: TypeId; lineTypeId: TypeId };

/**
 * The rows of another ArTable on the line's form version whose every key pair equals the line's.
 * Binds only when those rows hold exactly one distinct non-blank `valueTypeId`; both typeIds may be dotted.
 */
export type LedgerTableLookupSource = {
    kind: 'tableLookup';
    tableId: ArTableId;
    keys: LedgerTableLookupKey[];
    valueTypeId: TypeId;
};

/** One source that reads one value for the line: its own cell, a reference hop, or a same-form table lookup. */
export type LedgerRowValueSource = FormDeriveSourceCellSource | FormDeriveRowReferencePropSource | LedgerTableLookupSource;

/** The first of `sources` that binds, in order; never nested. */
export type LedgerFirstBoundSource = { kind: 'firstBound'; sources: LedgerRowValueSource[] };

export type LedgerBindSource = LedgerRowValueSource | LedgerFirstBoundSource;

export type LedgerAccrualBind = {
    claimAmount: LedgerBindSource;
    claimQuantity?: LedgerBindSource;
    triggerDate: LedgerBindSource;
    matchGroup: LedgerBindSource;
    counterparty: LedgerBindSource;
    grainRef?: LedgerBindSource;
    /** Keyed by a declared `dimensionId`. */
    dimensions?: Record<string, LedgerBindSource>;
};

export type LedgerAccrualRule = {
    ruleId: string;
    claimKindId: string;
    source: { formTypeIds: string[]; tableId: ArTableId };
    /** Evaluated by `matchesRowFilters` on formula-fresh cells; operands are literals only. */
    when: WorkflowRowFilter[];
    bind: LedgerAccrualBind;
};

/** Matches a row of the line table: its `header` cell is blank, or its trimmed text equals one of `equals`, ignoring case. */
export type LedgerStatementRowTest = { header: string; is: 'blank' } | { header: string; equals: string[] };

/**
 * `cell`: an A1 address on the mapping's sheet. `besideText`: the first non-blank cell right of the first cell above the
 * header row whose trimmed text equals `text`, ignoring case, passing over cells that repeat the label (a merged label
 * reads as its text in every cell of the range). `column`: the one distinct non-blank `header` value over the posting rows. `row`: the `header` cell of the first data row `where` admits; that row never posts.
 */
export type LedgerStatementValueSource =
    | { kind: 'cell'; address: string }
    | { kind: 'besideText'; text: string }
    | { kind: 'column'; header: string }
    | { kind: 'row'; where: LedgerStatementRowTest; header: string };

/** How one counterparty's statement file reads as one receipt; headers match by trimmed, case-insensitive text. */
export type LedgerStatementMapping = {
    mappingId: string;
    label: string;
    counterpartyId: string;
    instrument: LedgerInstrument;
    /** Absent: the first sheet. */
    sheet?: string;
    /** 1-based. */
    headerRow: number;
    /** Header texts; `dimensions` keyed by a declared dimensionId. */
    lines: { amount: string; matchGroup?: string; dimensions?: Record<string, string> };
    /** Tested in order; the first that matches skips the row. */
    skipRows: LedgerStatementRowTest[];
    total: LedgerStatementValueSource | { kind: 'sumOfLines' };
    /**
     * The column holding the total printed on the original statement as received, the same on every file split from
     * it. Optional in the file as well: a file without the column, or with it blank, reads no printed total.
     */
    originalTotal?: { kind: 'column'; header: string };
    reference?: LedgerStatementValueSource;
    receivedAt: LedgerStatementValueSource;
    /** Absent: 'dotDecimal' and 'monthFirst'. */
    numberLocale?: NumberLocale;
    dateLocale?: DateLocale;
    /** Row `headerRow` must hold exactly these headings, in this order, or the file is sent back before review. Absent: any headings. */
    templateHeadings?: string[];
    /**
     * True: a total that misses the lines' sum, credits included, by more than half a minor unit per rounded line (a
     * line, or the total, whose cell carried non-zero digits past the minor unit) sends the file back before review, and
     * a smaller gap is a `totalMismatch` warning. Absent or false: any gap is a `totalMismatch` warning.
     */
    totalMustMatch?: boolean;
    /** True: confirm refuses a converted file posted without the original statement as received. */
    requireOriginal?: boolean;
};

/** Statement files posted through `ledgerConfirmStatementImport`; one `cash:{channelId}` account for every mapping. */
export type LedgerFileChannel = { channelId: string; label: string; kind: 'file'; mappings: LedgerStatementMapping[] };

export type LedgerChannel = { channelId: string; label: string; kind: 'manual' } | LedgerFileChannel;

export type LedgerMatchKey = { role: 'matchGroup' } | { role: 'dimension'; dimensionId: string };

export type LedgerMatchTolerance =
    | { kind: 'minorUnits'; minorUnits: number }
    | { kind: 'percent'; percent: number };

export type LedgerMatchRule = {
    matchRuleId: string;
    label: string;
    counterpartyIds?: string[];
    keys: LedgerMatchKey[];
    amountFrom: 'remittanceLine' | 'receiptTotal';
    tolerance: LedgerMatchTolerance;
    score: number;
    /** Auto-commit is refused in v1; every allocation is committed by a person. */
    autoCommitAbove?: never;
};

export type LedgerTermTrigger = { role: 'triggerDate' } | { role: 'dimension'; dimensionId: string };

/** `trigger` is a key of `LedgerTermsBinding.triggerRoles`. */
export type LedgerTermShape =
    | { shape: 'daysAfterTrigger'; trigger: string; days: number }
    | { shape: 'monthLagDayOfMonth'; trigger: string; monthsAfter: number; dayOfMonth: number };

export const LEDGER_TERM_PARAMS = ['days', 'monthsAfter', 'dayOfMonth', 'trigger'] as const;
export type LedgerTermParam = (typeof LEDGER_TERM_PARAMS)[number];

/**
 * Where a claim's terms come from: entity records linked to the counterparty, read once at accrual by
 * `resolveLedgerClaimTerms` and copied into the claim. `params` maps each shape parameter to the record
 * field holding it.
 */
export type LedgerTermsBinding = {
    /** Absent `counterpartyReferenceKey`: the terms record is the counterparty's own record, so `entityId` is `counterparty.entityId`. */
    source: { entityId: string; counterpartyReferenceKey?: string; selectBy?: WorkflowRowFilter[] };
    /** A numeric field; the lowest value wins, and a tie at the top is `termsConflict`. */
    priorityField?: string;
    /** Absent: no record supplies a shape, and every claim takes `fallback` flagged `noTermsRecord`. */
    shapeField?: string;
    params?: Partial<Record<LedgerTermParam, string>>;
    triggerRoles: Record<string, LedgerTermTrigger>;
    fallback?: LedgerTermShape;
};

export const LEDGER_DEFAULT_UNAPPLIED_AFTER_DAYS = 30;
/** Strictly ascending bucket edges, in whole days past the expected date; `unappliedAfterDays`, absent reading as `LEDGER_DEFAULT_UNAPPLIED_AFTER_DAYS`, is the whole local days after receipt from which unapplied cash is an exception. */
export type LedgerAgingBuckets = { daysPastExpected: number[]; unappliedAfterDays?: number };

export type LedgerAdjustmentTarget = 'claim' | 'payment';

/** Posts to the one account `adjustment:{reasonId}`; `targets` names the sides it may close. */
export type LedgerAdjustmentReason = { reasonId: string; label: string; targets: LedgerAdjustmentTarget[] };

/** Luxon-compatible; whole units only, at least one above zero. */
export type LedgerDuration = { years?: number; months?: number; weeks?: number; days?: number };

/** The units a `LedgerDuration` may hold; the validator and the settings page's duration editor both read this list. */
export const LEDGER_DURATION_UNITS = ['years', 'months', 'weeks', 'days'] as const;
export type LedgerDurationUnit = (typeof LEDGER_DURATION_UNITS)[number];

/**
 * How long a ledger's records live: a resolved closure set in closed periods moves to the archive once its latest
 * transaction is `archiveAfter` old, and is purged `retainFor` after archiving. `schedule.cron` is a five-field cron
 * in the calendar structure's zone. `sourceFileRetention` and `outboxTtl` are stored but not yet enforced.
 */
export type LedgerLifecyclePolicy = {
    archiveAfter: LedgerDuration;
    retainFor: LedgerDuration;
    proposalRetention: LedgerDuration;
    sourceFileRetention: LedgerDuration;
    outboxTtl: LedgerDuration;
    schedule: { cron: string };
};

export type LedgerAccess = { readRight: UserRightId; postRight: UserRightId; commitRight: UserRightId };

/**
 * One ledger's whole configuration, valid as a whole or not at all. `rev` bumps on every semantic
 * change and is recorded on each transaction it produced; `description` and every `label` are never
 * semantic and never bump it. Only `ledgerSaveDefinition` bumps the rev, and `ledgerId` is immutable.
 */
export type LedgerDefinition = {
    ledgerId: string;
    label: string;
    description?: string;
    rev: number;
    /** ISO-4217 code; minor units follow the currency's own digits. */
    currency: string;
    calendar: LedgerCalendarSettings;
    counterparty: { entityId: string };
    /** What the screens call a match group, as "Order ref"; absent, they say "Match group". A label: never semantic, never bumps `rev`. */
    matchGroupLabel?: string;
    dimensions: LedgerDimension[];
    claimKinds: LedgerClaimKind[];
    accrualRules: LedgerAccrualRule[];
    channels: LedgerChannel[];
    matchRules: LedgerMatchRule[];
    terms?: LedgerTermsBinding;
    aging: LedgerAgingBuckets;
    /** Read as `[]` when absent, so a definition stored before reasons existed stays valid and allows no adjustment. */
    adjustmentReasons: LedgerAdjustmentReason[];
    /**
     * The declared reason, targeting `claim`, a statement credit line posts under at Match: the claim takes back the
     * credit and this reason lowers its commission by the same amount. Absent: a statement with a credit line is sent
     * back at import as `creditLines`, and the engine places no credit.
     */
    creditLineReasonId?: string;
    access: LedgerAccess;
    /** Absent: the ledger is never archived or purged. */
    lifecycle?: LedgerLifecyclePolicy;
};

/** The stored `ledgerDefinitions/{ledgerId}` document: the definition plus the save stamps, which `validateLedgerDefinition` strips. */
export type LedgerDefinitionDocument = LedgerDefinition & { modifiedAt: LedgerMoment; modifiedBy: string };

/** `path` is dotted from the definition root, e.g. `accrualRules[1].bind.claimAmount.typeId`; `''` is the root. */
export type LedgerDefinitionProblem = { path: string; message: string };

export type LedgerDefinitionCheck =
    | { valid: true; definition: LedgerDefinition }
    | { valid: false; problems: LedgerDefinitionProblem[] };

/**
 * The row an accrual came from. Carries no `versionId`: a new form version copies rows under the
 * same row ids, and the line must not accrue again. `formId` stays, since duplicated forms reuse row ids.
 */
export type LedgerSourceLine = { formId: string; tableId: ArTableId; rowId: ArRowId };

/** Every deterministic transaction-id form, one arm per posting source. */
export type LedgerTransactionIdSource =
    | { source: 'accrual'; ruleId: string; sourceLine: LedgerSourceLine; grainRef: string | null }
    | { source: 'signify'; channelId: string; invoice: string; lineItemNum: string; deliveryNumber: string }
    /**
     * A null `index` mints the receipt of the file's `importNumber`th import, counted from 1: `file:{fileHash}` for the
     * first, `file:{fileHash}:import:{importNumber}` for each import after its predecessors were all reversed.
     */
    | { source: 'file'; fileHash: string; index: null; importNumber: number }
    /** A number `index` mints the file's 1-based statement row id, the same on every import of the file. */
    | { source: 'file'; fileHash: string; index: number }
    | { source: 'manual'; uid: string; entryKey: string }
    | { source: 'allocation'; proposalId: string; claimId: string }
    | { source: 'proposalAdjustment'; proposalId: string; targetId: string }
    | { source: 'reversal'; reversesTransactionId: string }
    /** `periodKey` is the period's local start date; `closeNumber` counts the closes of that period from 1, so a reopened period can close again. */
    | { source: 'periodClose'; periodKey: string; closeNumber: number };
